import { useState, useEffect, ChangeEvent, useMemo } from 'react';
import { SelectChangeEvent, Theme, useMediaQuery } from '@mui/material';

import {
  ApplyingCollege,
  ApplyingCollegeWithSchool,
  DeadlineType,
  DeadlineTypeOption,
  ApplicationType,
  Decision,
  AdmissionStatus,
  ApplicationStatus,
} from '@common-types';

import useApplyingSchoolsPlannerStore from '@store/ApplyingSchoolsPlannerStore';
import useCommonAppStore from '@store/CommonAppStore';

import { MAX_CHECKPOINT_LABEL_LENGTH } from '@constants/applyingSchoolsPlanner';

import { ConfirmationMessage } from '@components/ConfirmationMessage';
import { FormState } from '@interfaces/applications';

import DeleteIcon from '@images/big-bucket.svg?react';

import PlannerSchoolCardHeader from './components/PlannerSchoolCardHeader';
import PlannerSchoolCardChecklist from './components/PlannerSchoolCardChecklist';
import PlannerSchoolCardRecommenders from './components/PlannerSchoolCardRecommenders';
import PlannerSchoolCardExpandButton from './components/PlannerSchoolCardExpandButton';
import PlannerSchoolCardAddCheckpoint from './components/PlannerSchoolCardAddCheckpoint';
import PlannerSchoolCardApplication from './components/PlannerSchoolCardApplication';
import EditSchoolCardApplication from './components/EditSchoolCardApplication';

import {
  StyledCard,
  StyledCardBody,
  StyledCardBodyLeft,
  StyledCardBodyRight,
  StyledCardDivider,
} from './PlannerSchoolCard.styles';

interface PlannerSchoolCardProps {
  applyingSchool: ApplyingCollegeWithSchool;
  isExtended: boolean;
  deadlineTypeOptions: DeadlineTypeOption[];
  onRemoveSchool: (id: number) => Promise<void>;
  onExtendSchool: (id: number | undefined) => void;
}

export const PlannerSchoolCard = ({
  applyingSchool,
  isExtended,
  deadlineTypeOptions,
  onRemoveSchool,
  onExtendSchool,
}: PlannerSchoolCardProps) => {
  const {
    id,
    memberId,
    school,
    application,
    admissionStatus,
    priority,
    decision,
    defaultChecklist,
    customChecklist = [],
  } = applyingSchool;

  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { applicant } = useCommonAppStore();

  const member = useMemo(
    () => applicant?.members?.find((applicantMember) => applicantMember.memberId === memberId),
    [applicant, memberId],
  );

  const initialValues = () => {
    return {
      member,
      admissionStatus: admissionStatus || '',
      deadlineType: application?.deadlineType || '',
      applicationType: application?.type || '',
      applicationStatus: application?.status || '',
      priority,
      decision: decision || '',
      defaultChecklist,
      customChecklist,
      customCheckpoint: {
        label: '',
        value: false,
      },
      helperText: '',
    };
  };

  const [deadline, setDeadline] = useState<string>(application?.deadline || '');
  const [initialState, setInitialState] = useState<FormState>(initialValues());
  const [formState, setFormState] = useState<FormState>(initialState);
  const [isShowTextField, setIsShowTextField] = useState(false);
  const [isCheckpointSave, setIsCheckpointSave] = useState(false);
  const [isApplicationSaving, setIsApplicationSaving] = useState(false);
  const [isOpenConfirmationMessage, setIsOpenConfirmationMessage] = useState(false);
  const [isEditChecklist, setIsEditChecklist] = useState(false);
  const [isEditApplication, setIsEditApplication] = useState(false);

  const { editAppliedSchool } = useApplyingSchoolsPlannerStore((state) => state);

  useEffect(() => {
    const formState = initialValues();
    setInitialState(formState);
    setFormState(formState);
  }, [applyingSchool]);

  useEffect(() => {
    updateChecklistChanges();
  }, [isCheckpointSave]);

  const updateFormValues = (fieldUpdates: Partial<FormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...fieldUpdates,
    }));
  };

  const updateChecklistChanges = async () => {
    if (isCheckpointSave && !isApplicationSaving) {
      const data: Partial<ApplyingCollegeWithSchool> = {
        id,
        memberId,
        priority,
        admissionStatus: admissionStatus as AdmissionStatus,
        application: {
          deadline: application?.deadline,
          deadlineType: application?.deadlineType as DeadlineType,
          type: application?.type as ApplicationType,
          status: application?.status as ApplicationStatus,
        },
        defaultChecklist: formState.defaultChecklist,
        customChecklist: formState.customChecklist,
        decision: decision as Decision,
      };

      await editAppliedSchool(data, applyingSchool.school.name ?? '');

      setIsCheckpointSave(false);
    }
  };

  const handleOnSaveApplication = async () => {
    if (isApplicationSaving) return;

    setIsApplicationSaving(true);

    const data: Partial<ApplyingCollegeWithSchool> = {
      id,
      memberId,
      priority: formState.priority,
      admissionStatus: formState.admissionStatus as AdmissionStatus,
      application: {
        deadline,
        deadlineType: formState.deadlineType as DeadlineType,
        type: formState.applicationType as ApplicationType,
        status: formState.applicationStatus as ApplicationStatus,
      },
      defaultChecklist,
      customChecklist,
      decision: formState.decision as Decision,
    };

    await editAppliedSchool(data, applyingSchool.school.name ?? '');

    setIsApplicationSaving(false);
    setIsEditApplication(false);
  };

  const handleOnSaveChecklist = () => setIsCheckpointSave(true);

  const handleOnCancelChecklist = () => {
    updateFormValues({
      defaultChecklist,
      customChecklist,
    });

    handleCloseTextField();
  };

  const handleOnCancelApplication = () => {
    updateFormValues({
      admissionStatus: admissionStatus || '',
      deadlineType: application?.deadlineType || '',
      applicationType: application?.type || '',
      applicationStatus: application?.status || '',
      priority,
      decision: decision || '',
    });

    setIsEditApplication(false);
  };

  const handleToggleExtended = () => onExtendSchool(isExtended ? undefined : memberId ?? applyingSchool.id);

  const handleOpenTextField = () => {
    setIsShowTextField(true);
  };

  const handleCloseTextField = () => {
    setIsShowTextField(false);

    updateFormValues({ customCheckpoint: initialState.customCheckpoint, helperText: initialState.helperText });
  };

  const handleAddCustomCheckpoint = () => {
    if (!formState.customCheckpoint.label.trim()) {
      updateFormValues({
        helperText: 'short',
      });

      return;
    }

    if (
      formState.customChecklist?.some(
        (task) => task.label.toLowerCase() === formState.customCheckpoint.label.trim().toLowerCase(),
      )
    ) {
      updateFormValues({
        helperText: 'duplicate',
      });

      return;
    }

    updateFormValues({
      customChecklist: [
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...formState.customChecklist!,
        { label: formState.customCheckpoint.label.trim(), value: formState.customCheckpoint.value },
      ],
    });

    handleCloseTextField();

    handleOnSaveChecklist();
  };

  const handleOpenConfirmationMessage = () => {
    setIsOpenConfirmationMessage(true);
  };

  const handleRemoveSchool = async () => {
    if (!id) return;

    await onRemoveSchool(id);
  };

  const handleDefaultCheckBoxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const updatedChecklist = {
      ...formState.defaultChecklist,
      [event.target.value]: checked,
    };

    updateFormValues({
      defaultChecklist: updatedChecklist,
    });

    handleOnSaveChecklist();
  };

  const handleCustomCheckBoxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!formState.customChecklist) {
      return;
    }

    const updatedChecklist = [...formState.customChecklist];

    updatedChecklist.forEach((task) => {
      if (task.label === event.target.value) {
        task.value = checked;
      }
    });

    updateFormValues({
      customChecklist: updatedChecklist,
    });

    handleOnSaveChecklist();
  };

  const handleCustomCheckpointChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    updateFormValues({
      customCheckpoint: { label: formState.customCheckpoint.label, value: checked },
    });
  };

  const handleCustomCheckpointLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > MAX_CHECKPOINT_LABEL_LENGTH) return;

    updateFormValues({
      customCheckpoint: { label: event.target.value, value: formState.customCheckpoint.value },
      helperText: initialState.helperText,
    });
  };

  const handleDeleteDefaultCheckpoint = (key: string) => {
    const updatedDefaultChecklist = {
      ...formState.defaultChecklist,
    };

    delete (updatedDefaultChecklist as Record<string, unknown>)[key];

    updateFormValues({ defaultChecklist: updatedDefaultChecklist });
  };

  const handleDeleteCustomCheckpoint = (label: string) => {
    updateFormValues({ customChecklist: formState?.customChecklist?.filter((task) => task.label !== label) });
  };

  const handleApplicationSelectChange = (formField: keyof FormState) => (event: SelectChangeEvent<unknown>) => {
    if (formField === 'deadlineType') {
      setDeadline(deadlineTypeOptions.find((option) => option.deadlineType === event.target.value)?.deadline || '');
    }

    updateFormValues({ [formField]: event.target.value });
  };

  const handleApplicationPriorityChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateFormValues({ priority: +event.target.value });
  };

  return (
    <StyledCard isExtended={isExtended}>
      <PlannerSchoolCardHeader
        isMobileScreen={isMobileScreen}
        applyingSchool={applyingSchool}
        isExtended={isExtended}
        onExtendedToggle={handleToggleExtended}
        onRemoveSchool={handleOpenConfirmationMessage}
        memberId={memberId}
      />

      {isExtended && (
        <>
          <StyledCardDivider />

          <StyledCardBody>
            <StyledCardBodyLeft>
              <PlannerSchoolCardChecklist
                schoolName={applyingSchool.school.name ?? ''}
                formState={formState}
                isEditApplication={isEditApplication}
                isEditChecklist={isEditChecklist}
                setIsEditChecklist={setIsEditChecklist}
                onDefaultCheckBoxChange={handleDefaultCheckBoxChange}
                onCustomCheckBoxChange={handleCustomCheckBoxChange}
                onDeleteDefaultCheckpoint={handleDeleteDefaultCheckpoint}
                onDeleteCustomCheckpoint={handleDeleteCustomCheckpoint}
                onSave={handleOnSaveChecklist}
                onCancel={handleOnCancelChecklist}
              />
              {!isEditChecklist ? (
                <PlannerSchoolCardAddCheckpoint
                  formState={formState}
                  isShowTextField={isShowTextField}
                  onAddCustomCheckpoint={handleAddCustomCheckpoint}
                  onCloseTextField={handleCloseTextField}
                  onCustomCheckpointChange={handleCustomCheckpointChange}
                  onCustomCheckpointLabelChange={handleCustomCheckpointLabelChange}
                  onClickAddCheckpoint={handleOpenTextField}
                />
              ) : null}
              {member ? <PlannerSchoolCardRecommenders isMobileScreen={isMobileScreen} member={member} /> : null}
            </StyledCardBodyLeft>

            <StyledCardDivider />

            <StyledCardBodyRight>
              {isEditApplication ? (
                <EditSchoolCardApplication
                  formState={formState}
                  handleOnSave={handleOnSaveApplication}
                  handleOnCancel={handleOnCancelApplication}
                  onSelectChange={handleApplicationSelectChange}
                  onPriorityChange={handleApplicationPriorityChange}
                />
              ) : (
                <PlannerSchoolCardApplication
                  memberId={memberId}
                  formState={formState}
                  schoolName={applyingSchool.school.name ?? ''}
                  isEditChecklist={isEditChecklist}
                  isEditApplication={isEditApplication}
                  setIsEditApplication={setIsEditApplication}
                  onSelectChange={handleApplicationSelectChange}
                  onPriorityChange={handleApplicationPriorityChange}
                  onSave={handleOnSaveApplication}
                  onCancel={handleOnCancelApplication}
                />
              )}
            </StyledCardBodyRight>
          </StyledCardBody>
        </>
      )}

      {isMobileScreen && (
        <>
          <StyledCardDivider />

          <PlannerSchoolCardExpandButton isExtended={isExtended} onExtendedToggle={handleToggleExtended} />
        </>
      )}

      {isOpenConfirmationMessage && (
        <ConfirmationMessage
          icon={<DeleteIcon />}
          title="Remove school"
          // eslint-disable-next-line max-len
          subtitle={`You are about to remove ${school?.name} from the list. Any progress with the application will not be saved, are you sure you want to remove this school from the list?`}
          isOpenConfirmationMessage={isOpenConfirmationMessage}
          setIsOpenConfirmationMessage={setIsOpenConfirmationMessage}
          onGoNextBtnClick={handleRemoveSchool}
        />
      )}
    </StyledCard>
  );
};
